import { GraduationCap, Radar, Scaling } from "lucide-react";
import { Reveal } from "./Reveal";

const ContourGrid = () => (
  <svg
    className="absolute inset-0 h-full w-full opacity-[0.07] pointer-events-none"
    aria-hidden="true"
    preserveAspectRatio="none"
    viewBox="0 0 100 100"
  >
    {Array.from({ length: 11 }).map((_, i) => (
      <line key={`v${i}`} x1={i * 10} y1="0" x2={i * 10} y2="100" stroke="white" strokeWidth="0.15" />
    ))}
    {Array.from({ length: 7 }).map((_, i) => (
      <line key={`h${i}`} x1="0" y1={i * 16.6} x2="100" y2={i * 16.6} stroke="white" strokeWidth="0.15" />
    ))}
  </svg>
);

export default function WhyEngage() {
  return (
    <section
      id="why-coastal"
      data-testid="why-engage-section"
      className="relative py-24 md:py-32 bg-[#07131F] border-y border-white/5 overflow-hidden"
    >
      <ContourGrid />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal>
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-teal-400 font-medium">
            Why engage Coastal
          </p>
          <h2 className="mt-4 font-display text-3xl md:text-5xl tracking-tight leading-tight text-slate-50 max-w-3xl">
            Senior capability, engaged on your terms.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-12 gap-4 md:gap-5">
          <Reveal className="col-span-12 md:col-span-8" delay={0.05}>
            <div
              data-testid="why-fast-diagnosis-card"
              className="group relative h-full bg-[#02060D] border border-white/10 p-8 md:p-12 hover:border-teal-400/40 transition-colors duration-500"
            >
              <div className="flex items-start justify-between">
                <Radar className="h-8 w-8 text-teal-400" strokeWidth={1.5} />
                <span className="font-display text-6xl md:text-8xl leading-none text-white/5 group-hover:text-teal-400/10 transition-colors duration-500">
                  01
                </span>
              </div>
              <h3 className="mt-10 md:mt-16 font-display text-2xl md:text-4xl text-slate-50">
                Fast diagnosis
              </h3>
              <p className="mt-4 max-w-xl text-base md:text-lg leading-relaxed text-slate-300">
                Forty years of pattern recognition means the real problem surfaces quickly.
                Coastal fixes the cause, not the symptom — and tells you plainly what it will take.
              </p>
            </div>
          </Reveal>

          <div className="col-span-12 md:col-span-4 flex flex-col gap-4 md:gap-5">
            <Reveal delay={0.12} className="flex-1">
              <div
                data-testid="why-flexible-capacity-card"
                className="group h-full bg-[#02060D] border border-white/10 p-8 hover:border-teal-400/40 transition-colors duration-500"
              >
                <div className="flex items-start justify-between">
                  <Scaling className="h-7 w-7 text-teal-400" strokeWidth={1.5} />
                  <span className="font-display text-4xl leading-none text-white/5 group-hover:text-teal-400/10 transition-colors duration-500">
                    02
                  </span>
                </div>
                <h3 className="mt-8 font-display text-xl md:text-2xl text-slate-50">
                  Flexible capacity
                </h3>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-slate-400">
                  Senior hands for exactly as long as you need them — days, weeks or months.
                  No recruitment cycle, no overhead, no permanent headcount.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.18} className="flex-1">
              <div
                data-testid="why-capability-transfer-card"
                className="group h-full bg-[#02060D] border border-white/10 p-8 hover:border-teal-400/40 transition-colors duration-500"
              >
                <div className="flex items-start justify-between">
                  <GraduationCap className="h-7 w-7 text-teal-400" strokeWidth={1.5} />
                  <span className="font-display text-4xl leading-none text-white/5 group-hover:text-teal-400/10 transition-colors duration-500">
                    03
                  </span>
                </div>
                <h3 className="mt-8 font-display text-xl md:text-2xl text-slate-50">
                  Capability transfer
                </h3>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-slate-400">
                  We mentor your people as we work — leaving stronger teams, better processes
                  and lasting capability behind, not dependency.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
